import React from 'react';
import { Alert, Box, Button, Typography } from 'housecall-ui';
import Chip from 'housecall-ui/dist/DesignSystem/DataDisplay/Chip/Chip';
import Lock from 'housecall-ui/dist/IconsAndImages/Icons/Lock';

import { OrganizationInfo } from '../../api/api';
import { InvoiceData } from '../../api/payment/api';
import { Layout } from '../../components/Layout';
import { ScrollableContent } from '../../components/ScrollableContent';
import { StepProgressBar } from '../../components/StepProgressBar';
import { useOrgData } from '../../hooks/useOrgData';
import { CartItem } from '../../state/types';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { calculateSubTotalPrice, calculateTotalPrice } from '../../utils/calculatePrice';

import CreditCardForm from './CreditCardForm';
import InvoiceDetails from './InvoiceDetails';
import { getButtonLabel } from './PaymentStep';
import { getDepositAmount } from './utils';

const invoiceDataMock: Partial<InvoiceData> = {
  invoiceNumber: 'INV-12345',
  paymentOptions: {
    ccEnabled: true,
    achEnabled: false,
    canSaveCardOnFile: true,
    canPayOnline: true,
  },
  status: 'unpaid',
  invoiceCount: 1,
  template: 'deposit',
};

const generateInvoiceData = (orgData: OrganizationInfo, cart: CartItem[]): InvoiceData => {
  const hasDeposit = orgData.paymentSettings?.config === 'deposit';
  const orgTax = orgData && 'taxInfo' in orgData ? orgData?.taxInfo : null;

  const subtotal = calculateSubTotalPrice(cart);
  const total = calculateTotalPrice(cart, orgTax?.rate || null);

  const depositAmount = getDepositAmount(orgData.paymentSettings, total);

  const result: InvoiceData = {
    ...invoiceDataMock,
    total,
    subtotal,
    dueAmount: hasDeposit ? depositAmount : total,
    amount: hasDeposit ? depositAmount : total,
    template: orgData.paymentSettings?.config,
  } as InvoiceData;

  return result;
};

export const PaymentStepPreview = () => {
  const {
    state: { token, cart },
  } = useOnlineBookingReducer();
  const { data: orgData } = useOrgData({ token });

  if (!orgData) {
    return null;
  }

  const onClick = () => {
    document.querySelector('#payment-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const invoiceData = generateInvoiceData(orgData, cart);

  return (
    <Layout
      Header={<StepProgressBar currentStep={5} stepCount={5} />}
      ContentComponent={
        <ScrollableContent>
          <Alert severity="warning">This is a preview page. No payments will be processed.</Alert>
          <Typography variant="h5" gutterBottom>
            Payment method
          </Typography>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body1">Enter card details below</Typography>
            <Chip icon={<Lock size="small" color="primary" />} size="small" label="Secure" color="info" />
          </Box>
          <Box mt={2}>
            <CreditCardForm isPreview />
          </Box>

          <Box my={2}>
            <InvoiceDetails invoiceData={invoiceData} />
          </Box>
        </ScrollableContent>
      }
      Buttons={
        <Button version="v2" size="medium" fullWidth variant="contained" color="secondary" onClick={onClick}>
          {getButtonLabel(invoiceData)}
        </Button>
      }
    />
  );
};
